import { useCallback, useState } from "react";
import { useParams } from "react-router";
import { useFetch } from "../../hooks/useFetch";

type DlqEntry = {
	id: string;
	endpointId: string;
	endpointUrl: string;
	eventType: string;
	error: string | null;
	attemptCount: number;
	createdAt: string;
	retriedAt: string | null;
};

type DlqListResponse = {
	data: DlqEntry[];
	pagination: {
		page: number;
		pageSize: number;
		total: number;
		totalPages: number;
	};
};

type TenantListResponse = {
	data: { slug: string; name: string }[];
	pagination: { total: number };
};

type InspectData = {
	id: string;
	payload: unknown;
	error: string | null;
	attemptCount: number;
	createdAt: string;
};

export function AdminDlqPage() {
	const { slug: urlSlug } = useParams<{ slug?: string }>();
	const [selectedSlug, setSelectedSlug] = useState(urlSlug ?? "");
	const [page, setPage] = useState(1);
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [inspecting, setInspecting] = useState<InspectData | null>(null);
	const [actionLoading, setActionLoading] = useState(false);

	const slug = urlSlug ?? selectedSlug;

	const { data: tenants } = useFetch<TenantListResponse>(
		"/api/admin/tenants?pageSize=200",
	);

	const { data, loading, error, refetch } = useFetch<DlqListResponse>(
		slug
			? `/api/admin/tenants/${slug}/webhooks/dlq?page=${page}&pageSize=20`
			: "",
	);

	const handleInspect = useCallback(
		async (id: string) => {
			if (!slug) return;
			const res = await fetch(`/api/admin/tenants/${slug}/webhooks/dlq/${id}`, {
				credentials: "include",
			});
			if (res.ok) {
				const payload = (await res.json()) as InspectData;
				setInspecting(payload);
			}
		},
		[slug],
	);

	const handleRetry = useCallback(
		async (id: string) => {
			if (!slug) return;
			setActionLoading(true);
			await fetch(`/api/admin/tenants/${slug}/webhooks/dlq/${id}/retry`, {
				method: "POST",
				credentials: "include",
			});
			setActionLoading(false);
			refetch();
		},
		[slug, refetch],
	);

	const handleBulkRetry = useCallback(async () => {
		if (!slug || selectedIds.size === 0) return;
		setActionLoading(true);
		await fetch(`/api/admin/tenants/${slug}/webhooks/dlq/bulk-retry`, {
			method: "POST",
			credentials: "include",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ ids: [...selectedIds] }),
		});
		setSelectedIds(new Set());
		setActionLoading(false);
		refetch();
	}, [slug, selectedIds, refetch]);

	const handlePurge = useCallback(async () => {
		if (!slug) return;
		setActionLoading(true);
		await fetch(`/api/admin/tenants/${slug}/webhooks/dlq`, {
			method: "DELETE",
			credentials: "include",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({}),
		});
		setActionLoading(false);
		refetch();
	}, [slug, refetch]);

	function toggleId(id: string) {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}

	return (
		<div>
			<h1 className="text-2xl font-bold text-gray-900 mb-4">
				Webhook Dead Letter Queue
			</h1>

			{!urlSlug && (
				<div className="mb-4">
					<label
						htmlFor="tenant-select"
						className="block text-xs text-gray-600 mb-1"
					>
						Tenant
					</label>
					<select
						id="tenant-select"
						value={selectedSlug}
						onChange={(e) => {
							setSelectedSlug(e.target.value);
							setPage(1);
							setSelectedIds(new Set());
						}}
						className="text-sm border border-gray-300 rounded px-2 py-1"
					>
						<option value="">Select a tenant…</option>
						{tenants?.data.map((t) => (
							<option key={t.slug} value={t.slug}>
								{t.name} ({t.slug})
							</option>
						))}
					</select>
				</div>
			)}

			{!slug && (
				<p className="text-gray-500 text-sm">
					Select a tenant to view DLQ entries.
				</p>
			)}

			{error && <p className="text-red-600 text-sm mb-3">{error}</p>}

			{slug && loading && !data && (
				<p className="text-gray-500 text-sm">Loading…</p>
			)}

			{slug && data && (
				<>
					<div className="flex gap-2 mb-3">
						<button
							type="button"
							disabled={selectedIds.size === 0 || actionLoading}
							onClick={handleBulkRetry}
							className="text-sm px-3 py-1 rounded border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-50"
						>
							Retry Selected ({selectedIds.size})
						</button>
						<button
							type="button"
							disabled={actionLoading}
							onClick={handlePurge}
							className="text-sm px-3 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
						>
							Purge Retried
						</button>
					</div>

					<div className="overflow-x-auto">
						<table className="w-full text-sm text-left">
							<thead className="bg-gray-100 text-gray-700">
								<tr>
									<th className="px-4 py-2 w-8">
										<span className="sr-only">Select</span>
									</th>
									<th className="px-4 py-2">Endpoint URL</th>
									<th className="px-4 py-2">Event Type</th>
									<th className="px-4 py-2">Error</th>
									<th className="px-4 py-2">Attempts</th>
									<th className="px-4 py-2">Created At</th>
									<th className="px-4 py-2">Actions</th>
								</tr>
							</thead>
							<tbody>
								{data.data.map((entry) => (
									<tr key={entry.id} className="border-b border-gray-200">
										<td className="px-4 py-2">
											<input
												type="checkbox"
												checked={selectedIds.has(entry.id)}
												onChange={() => toggleId(entry.id)}
											/>
										</td>
										<td className="px-4 py-2 font-mono text-xs truncate max-w-[200px]">
											{entry.endpointUrl}
										</td>
										<td className="px-4 py-2">
											<span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
												{entry.eventType}
											</span>
										</td>
										<td className="px-4 py-2 text-xs text-gray-600 truncate max-w-[200px]">
											{entry.error ?? "—"}
										</td>
										<td className="px-4 py-2">{entry.attemptCount}</td>
										<td className="px-4 py-2 text-xs text-gray-600">
											{new Date(entry.createdAt).toLocaleString()}
										</td>
										<td className="px-4 py-2 flex gap-1">
											<button
												type="button"
												onClick={() => handleInspect(entry.id)}
												className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
											>
												Inspect
											</button>
											<button
												type="button"
												disabled={actionLoading}
												onClick={() => handleRetry(entry.id)}
												className="text-xs px-2 py-1 rounded border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-50"
											>
												Retry
											</button>
										</td>
									</tr>
								))}
								{data.data.length === 0 && (
									<tr>
										<td
											colSpan={7}
											className="px-4 py-8 text-center text-gray-500"
										>
											No pending DLQ entries.
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>

					<div className="flex items-center justify-between mt-4">
						<button
							type="button"
							disabled={page <= 1}
							onClick={() => setPage((p) => p - 1)}
							className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
						>
							Previous
						</button>
						<span className="text-sm text-gray-600">
							Page {data.pagination.page} of {data.pagination.totalPages || 1}
						</span>
						<button
							type="button"
							disabled={page >= data.pagination.totalPages}
							onClick={() => setPage((p) => p + 1)}
							className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
						>
							Next
						</button>
					</div>
				</>
			)}

			{inspecting && (
				// biome-ignore lint/a11y/noStaticElementInteractions: modal backdrop dismiss
				<div
					role="presentation"
					className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
					onClick={() => setInspecting(null)}
					onKeyDown={(e) => {
						if (e.key === "Escape") setInspecting(null);
					}}
				>
					<div
						role="dialog"
						aria-modal="true"
						className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-auto p-6"
						onClick={(e) => e.stopPropagation()}
						onKeyDown={(e) => e.stopPropagation()}
					>
						<div className="flex items-center justify-between mb-4">
							<h2 className="text-lg font-semibold text-gray-900">
								DLQ Entry — {inspecting.id.slice(0, 8)}…
							</h2>
							<button
								type="button"
								onClick={() => setInspecting(null)}
								className="text-gray-400 hover:text-gray-600 text-xl"
							>
								&times;
							</button>
						</div>
						<div className="space-y-3">
							<div>
								<p className="text-xs text-gray-500 font-medium">Error</p>
								<p className="text-sm text-red-600">
									{inspecting.error ?? "—"}
								</p>
							</div>
							<div>
								<p className="text-xs text-gray-500 font-medium">
									Attempts: {inspecting.attemptCount}
								</p>
							</div>
							<div>
								<p className="text-xs text-gray-500 font-medium mb-1">
									Decrypted Payload
								</p>
								<pre className="bg-gray-50 border rounded p-3 text-xs overflow-auto max-h-[400px]">
									{JSON.stringify(inspecting.payload, null, 2)}
								</pre>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
